import { useEffect, useState } from 'react'
import { Megaphone, Loader2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

interface Broadcast {
  id: string
  title: string
  message: string
  severity: string
  target_audience: string
  starts_at: string | null
  ends_at: string | null
  click_count: number
  impression_count: number
  created_at: string
}

const severityColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500/10 text-red-500'
    case 'warning':
      return 'bg-amber-500/10 text-amber-500'
    case 'maintenance':
      return 'bg-blue-500/10 text-blue-500'
    default:
      return 'bg-emerald-500/10 text-emerald-500'
  }
}

export function BroadcastAdmin() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    title: '',
    message: '',
    severity: 'info',
    target_audience: 'all',
    target_plans: '',
    target_account_ids: '',
    ends_at: '',
  })

  const fetchBroadcasts = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/broadcasts')
      setBroadcasts(res.data.broadcasts || [])
    } catch {
      toast.error('Failed to load broadcasts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBroadcasts()
  }, [])

  const handleCreate = async () => {
    if (!form.title || !form.message) {
      toast.error('Title and message are required')
      return
    }
    const payload: Record<string, unknown> = {
      title: form.title,
      message: form.message,
      severity: form.severity,
      target_audience: form.target_audience,
      target_plans: form.target_plans
        ? form.target_plans.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      target_account_ids: form.target_account_ids
        ? form.target_account_ids.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    }
    if (form.ends_at) {
      payload.ends_at = new Date(form.ends_at).toISOString()
    }
    try {
      await api.post('/admin/broadcast', payload)
      toast.success('Broadcast created')
      setOpen(false)
      setForm({
        title: '',
        message: '',
        severity: 'info',
        target_audience: 'all',
        target_plans: '',
        target_account_ids: '',
        ends_at: '',
      })
      fetchBroadcasts()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to create broadcast'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            Broadcasts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage platform announcements</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Broadcast
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Create Broadcast</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Scheduled maintenance"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What do you want to tell users?"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="severity">Severity</Label>
                  <Select
                    value={form.severity}
                    onValueChange={(value) => setForm({ ...form, severity: value })}
                  >
                    <SelectTrigger id="severity">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['info', 'warning', 'critical', 'maintenance'].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target">Target Audience</Label>
                  <Select
                    value={form.target_audience}
                    onValueChange={(value) => setForm({ ...form, target_audience: value })}
                  >
                    <SelectTrigger id="target">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All accounts</SelectItem>
                      <SelectItem value="specific_plans">Specific plans</SelectItem>
                      <SelectItem value="specific_accounts">Specific accounts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.target_audience === 'specific_plans' && (
                <div className="space-y-2">
                  <Label htmlFor="plans">Target Plans (comma-separated)</Label>
                  <Input
                    id="plans"
                    value={form.target_plans}
                    onChange={(e) => setForm({ ...form, target_plans: e.target.value })}
                    placeholder="starter, builder"
                  />
                </div>
              )}
              {form.target_audience === 'specific_accounts' && (
                <div className="space-y-2">
                  <Label htmlFor="accounts">Target Account IDs (comma-separated)</Label>
                  <Input
                    id="accounts"
                    value={form.target_account_ids}
                    onChange={(e) => setForm({ ...form, target_account_ids: e.target.value })}
                    placeholder="uuid1, uuid2"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="ends">Ends At (optional)</Label>
                <Input
                  id="ends"
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate}>Create Broadcast</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Title</th>
                  <th className="px-4 py-3 text-left font-medium">Severity</th>
                  <th className="px-4 py-3 text-left font-medium">Target</th>
                  <th className="px-4 py-3 text-left font-medium">Ends</th>
                  <th className="px-4 py-3 text-left font-medium">Impressions</th>
                  <th className="px-4 py-3 text-left font-medium">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : broadcasts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No broadcasts
                    </td>
                  </tr>
                ) : (
                  broadcasts.map((b) => (
                    <tr key={b.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium">{b.title}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-xs">{b.message}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={severityColor(b.severity)}>
                          {b.severity}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{b.target_audience.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {b.ends_at ? new Date(b.ends_at).toLocaleString() : <span className="italic">Never</span>}
                      </td>
                      <td className="px-4 py-3">{b.impression_count.toLocaleString()}</td>
                      <td className="px-4 py-3">{b.click_count.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
